/* Habitats for Heroes — nomination form.
 * Rendered into any element with [data-hfh-form] (see sections/habitats-heroes-nomination.liquid).
 * Submits to the internal nominations database (Netlify) at the URL in data-endpoint. */
(() => {
  "use strict";

  const STATES = [
    // Contiguous US only: Alaska and Hawaii are intentionally excluded.
    ["AL", "Alabama"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"], ["CO", "Colorado"],
    ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"],
    ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
    ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"],
    ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"],
    ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
    ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
    ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
    ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
    ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ];
  const DEFAULT_SPECIES = [
    "Bearded dragon", "Leopard gecko", "Crested gecko", "Ball python", "Corn snake", "Blue-tongued skink",
    "Tortoise", "Chameleon", "Tegu", "Uromastyx", "Frog / amphibian", "Not sure yet", "Other",
  ];
  const DEFAULT_SIZES = [
    "2'x2'x2'", "2'x2'x4'", "4'x2'x16\"", "4'x2'x2'", "4'x2'x4'",
    "Not sure — help us choose",
  ];
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const ZIP_RE = /^\d{5}(-\d{4})?$/;
  const WHY_MAX = 500;
  const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
  const DRAFT_KEY = "hfh-nomination-draft-v1";
  const SHIP_UNSURE_LABEL = "I'm not sure — that's okay, we'll reach out to them directly!";
  const SHIP_UNSURE_SUMMARY = "Not sure — Zen Habitats will ask them";

  // ---- small helpers --------------------------------------------------------
  const get = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
  const set = (obj, path, value) => {
    const keys = path.split(".");
    let o = obj;
    keys.slice(0, -1).forEach((k) => {
      if (typeof o[k] !== "object" || o[k] === null) o[k] = {};
      o = o[k];
    });
    o[keys[keys.length - 1]] = value;
  };
  const esc = (s) =>
    String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const val = (v, s) => (typeof v === "function" ? v(s) : v);
  const isSelf = (s) => s.nominatorType === "self";
  const who = (selfText, otherText) => (s) => (isSelf(s) ? selfText : otherText);
  const storage = {
    read() { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; } },
    write(v) { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(v)); } catch {} },
    clear() { try { localStorage.removeItem(DRAFT_KEY); } catch {} },
  };
  const lines = (text, fallback) => {
    const list = String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    return list.length ? list : fallback;
  };
  let uid = 0;

  // ---- form definition --------------------------------------------------------
  // Each step shows only when its `when` returns true; fields can also have their own `when`.
  function buildSteps(cfg) {
    const teacher = (s) => s.category === "teacher";
    const rescue = (s) => s.category === "rescue";
    return [
      {
        id: "start",
        title: "Let's get started",
        fields: [
          {
            name: "nominatorType", type: "cards", required: true,
            label: "Are you nominating yourself or someone else?",
            options: [
              { value: "self", label: "Myself", desc: "I'm the teacher, or I run the rescue" },
              { value: "other", label: "Someone else", desc: "I'm nominating a teacher, classroom or rescue I know" },
            ],
          },
          {
            name: "category", type: "cards", required: true,
            label: who("What are you applying as?", "Who are you nominating?"),
            options: [
              { value: "teacher", label: "A teacher or classroom", desc: "Teachers at accredited schools whose classroom keeps, or wants to keep, a reptile" },
              { value: "rescue", label: "A rescue organization", desc: "Nonprofit (501(c)(3)) animal rescues caring for animals in need" },
            ],
          },
        ],
      },
      {
        id: "you",
        title: "About you",
        subtitle: "So we can follow up with you about your nomination.",
        when: (s) => s.nominatorType === "other",
        fields: [
          { name: "nominator.name", label: "Your name", required: true, autocomplete: "name" },
          { name: "nominator.email", type: "email", label: "Your email", required: true, autocomplete: "email" },
          { name: "nominator.relationship", label: "Your relationship to the nominee", required: true, placeholder: "e.g. parent, student, colleague, volunteer" },
        ],
      },
      {
        id: "teacher",
        title: who("About you", "About the teacher"),
        when: teacher,
        fields: [
          { name: "teacher.name", label: who("Your name", "Teacher's name"), required: true, autocomplete: (s) => (isSelf(s) ? "name" : "off") },
          { name: "teacher.email", type: "email", label: who("Your email", "Teacher's email"), required: true, autocomplete: (s) => (isSelf(s) ? "email" : "off"),
            help: who("", "We'll only use this to reach the teacher about Habitats for Heroes.") },
          { name: "teacher.phone", type: "tel", label: who("Your phone", "Teacher's phone"), autocomplete: (s) => (isSelf(s) ? "tel" : "off"),
            help: "Optional — helps us reach the winner quickly." },
          { name: "teacher.gradeSubject", label: "Grade level or subject taught", required: true, placeholder: "e.g. 5th grade, AP Biology" },
          { name: "teacher.school", label: "School name", required: true },
          { name: "teacher.schoolAddress", type: "address", label: "School address", required: true },
        ],
      },
      {
        id: "classroom",
        title: "About the classroom",
        when: teacher,
        fields: [
          {
            name: "teacher.schoolAllowsAnimals", type: "choice", required: true,
            label: "Does the school allow animals in the classroom?",
            help: "Many schools need to approve classroom animals. If you're not sure, that's okay — we'll confirm before anything ships.",
            // No "No" option: classrooms that can't have an animal aren't eligible.
            options: [{ value: "yes", label: "Yes" }, { value: "unsure", label: "Not sure yet" }],
          },
          {
            name: "teacher.reptileStatus", type: "choice", required: true,
            label: "Does the classroom currently keep a reptile, or plan to?",
            // No "No" option: the giveaway is for classrooms that have or are getting a reptile.
            options: (s) => [
              { value: "has", label: isSelf(s) ? "Yes, we have one" : "Yes, they have one" },
              { value: "plans", label: isSelf(s) ? "We plan to" : "They plan to" },
            ],
          },
          {
            name: "teacher.reptileSpecies", required: true, placeholder: "e.g. leopard gecko",
            label: (s) => (get(s, "teacher.reptileStatus") === "plans" ? `Which species ${isSelf(s) ? "are you" : "are they"} planning on?` : "Which species?"),
            when: (s) => ["has", "plans"].includes(get(s, "teacher.reptileStatus")),
          },
          { name: "photo", type: "file", label: "Photo of the classroom or current setup", help: "Optional. JPG, PNG or HEIC." },
          {
            name: "teacher.shipToSchool", type: "choice", required: true,
            label: "If selected, should we ship to the school address?",
            // Someone nominating a teacher may not know where it should go; we follow up with the teacher.
            options: (s) => [
              { value: "yes", label: "Yes, ship to the school" },
              { value: "no", label: "No, use a different address" },
              ...(isSelf(s) ? [] : [{ value: "unsure", label: SHIP_UNSURE_LABEL }]),
            ],
          },
          { name: "teacher.shippingAddress", type: "address", label: "Shipping address", required: true, when: (s) => get(s, "teacher.shipToSchool") === "no" },
        ],
      },
      {
        id: "rescue",
        title: who("About your rescue", "About the rescue"),
        when: rescue,
        fields: [
          { name: "rescue.name", label: "Rescue name", required: true },
          { name: "rescue.web", label: "Website or social media handle", required: true, placeholder: "e.g. www.example.org or @rescuename" },
          {
            name: "rescue.registered", type: "choice", required: true,
            label: "Is the rescue a registered 501(c)(3) nonprofit?",
            help: "Rescues must have 501(c)(3) status to receive the prize. If you're not sure, we'll verify it.",
            // No "No" option: rescues without 501(c)(3) status aren't eligible.
            options: [{ value: "yes", label: "Yes" }, { value: "unsure", label: "Not sure" }],
          },
          { name: "rescue.registrationNumber", label: "EIN (federal tax ID number)", help: "Optional, but it speeds up our review.", when: (s) => get(s, "rescue.registered") === "yes" },
          {
            name: "rescue.animalsInCare", type: "textarea", rows: 3, max: 400, required: true,
            label: "What species, and roughly how many animals, does the rescue typically have in care?",
            placeholder: "e.g. usually 15–20 animals, mostly bearded dragons and ball pythons",
          },
          { name: "photo", type: "file", label: "Photo of the rescue or current setups", help: "Optional. JPG, PNG or HEIC." },
        ],
      },
      {
        id: "contact",
        title: who("Your contact & shipping details", "Rescue contact & shipping"),
        when: rescue,
        fields: [
          { name: "rescue.contactName", label: who("Your name", "Contact name"), required: true, autocomplete: (s) => (isSelf(s) ? "name" : "off"),
            help: who("", "The person at the rescue we should contact.") },
          { name: "rescue.contactEmail", type: "email", label: who("Your email", "Contact email"), required: true, autocomplete: (s) => (isSelf(s) ? "email" : "off") },
          { name: "rescue.phone", type: "tel", label: who("Your phone", "Contact phone"), help: "Optional — helps us reach the winner quickly." },
          {
            name: "rescue.shippingKnown", type: "choice", required: true, when: (s) => !isSelf(s),
            label: "Do you know where the enclosure should ship?",
            options: [{ value: "yes", label: "Yes, I'll enter the address" }, { value: "unsure", label: SHIP_UNSURE_LABEL }],
          },
          { name: "rescue.shippingAddress", type: "address", label: "Shipping address", required: true,
            help: "Where the enclosure would ship if the rescue is selected.",
            when: (s) => isSelf(s) || get(s, "rescue.shippingKnown") === "yes" },
        ],
      },
      {
        id: "story",
        title: "The nomination",
        fields: [
          {
            name: "story.why", type: "textarea", rows: 6, max: WHY_MAX, required: true,
            label: (s) => {
              const place = s.category === "rescue" ? "rescue" : "classroom";
              return `How would ${isSelf(s) ? "your" : "their"} ${place} benefit from a free enclosure setup?`;
            },
            help: (s) =>
              s.category === "rescue"
                ? "Tell us the story — the animals in their care and the difference it would make."
                : "Tell us the story — the students, the animals and the difference it would make.",
          },
          { name: "story.species", type: "select", label: "What animal is the enclosure for?", required: true, options: cfg.species },
          { name: "story.speciesOther", label: "Which animal?", required: true, when: (s) => get(s, "story.species") === "Other" },
          { name: "story.size", type: "sizes", label: "What size setup would help most?", required: true, options: cfg.sizes,
            help: "Sizes are length × depth × height." },
          { name: "story.sizeNote", label: "Anything else we should know about the setup?", placeholder: "e.g. space limits, current enclosure size" },
        ],
      },
      {
        id: "review",
        title: "Review & submit",
        subtitle: "Check everything looks right, then send it our way.",
        fields: [
          { type: "summary" },
          { name: "consent.nomineeAware", type: "checkbox", required: true, when: (s) => s.nominatorType === "other",
            label: "I confirm the nominee is aware of and okay with being nominated." },
          { name: "consent.adult", type: "checkbox", required: true, label: "I confirm I'm 18 or older." },
          { name: "consent.rules", type: "checkbox", required: true, rulesLink: true,
            label: "I agree to the official giveaway rules and eligibility terms." },
          { name: "consent.updates", type: "checkbox", label: "Keep me updated by email about Habitats for Heroes." },
        ],
      },
    ];
  }

  // ---- display helpers for the review summary --------------------------------
  const OPTION_TEXT = {
    nominatorType: { self: "Myself", other: "Someone else" },
    category: { teacher: "A teacher or classroom", rescue: "A rescue organization" },
    yesno: { yes: "Yes", no: "No", unsure: "Not sure yet" },
    reptileStatus: { has: "Has one", plans: "Plans to" },
  };
  const fmtAddress = (a) =>
    a && a.street ? [a.street, a.street2, `${a.city || ""}, ${a.state || ""} ${a.zip || ""}`.trim()].filter(Boolean).join("\n") : "";

  // ---- the form component -------------------------------------------------------
  function mount(root) {
    if (root.dataset.hfhReady) return;
    root.dataset.hfhReady = "1";

    const cfg = {
      endpoint: root.dataset.endpoint || "",
      rulesUrl: root.dataset.rulesUrl || "",
      privacyUrl: root.dataset.privacyUrl || "",
      species: lines(root.dataset.species, DEFAULT_SPECIES),
      sizes: lines(root.dataset.sizes, DEFAULT_SIZES),
    };
    const steps = buildSteps(cfg);
    const startedAt = Date.now();
    let state = { consent: {} };
    let photo = null; // { file, url }
    let current = "start";
    let submitting = false;
    let finished = false;
    let photoTask = null;

    const draft = storage.read();
    const restored = Boolean(draft && draft.state && draft.state.nominatorType);
    if (restored) {
      state = draft.state;
      current = draft.current || "start";
    }

    root.innerHTML = `
      <div class="hfh-card">
        <div class="hfh-progress" aria-hidden="true"><div class="hfh-progress__bar"></div></div>
        <p class="hfh-stepcount" aria-live="polite"></p>
        <div class="hfh-restored" hidden>
          <span>We saved your progress from last time.</span>
          <button type="button" class="hfh-link" data-action="restart">Start over</button>
        </div>
        <form class="hfh-body" novalidate></form>
      </div>`;
    const $ = (sel) => root.querySelector(sel);
    const form = $(".hfh-body");
    if (restored) $(".hfh-restored").hidden = false;

    const activeSteps = () => steps.filter((st) => !st.when || st.when(state));
    const stepById = (id) => steps.find((st) => st.id === id);

    let saveTimer;
    const saveDraft = () => {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => storage.write({ state, current }), 300);
    };

    // ---- field rendering ----
    function fieldHtml(f, key) {
      const id = `hfh-${++uid}`;
      const label = esc(val(f.label, state));
      const help = val(f.help, state);
      const req = f.required ? "" : ' <span class="hfh-optional">(optional)</span>';
      const helpHtml = help ? `<p class="hfh-help" id="${id}-help">${esc(help)}</p>` : "";
      const errHtml = `<p class="hfh-error" id="${id}-err" role="alert" hidden></p>`;
      const describedBy = `${help ? `${id}-help ` : ""}${id}-err`;
      const value = f.name ? get(state, f.name) : undefined;
      const wrap = (inner, tag = "div") =>
        `<${tag} class="hfh-field hfh-field--${f.type || "text"}" data-key="${key}">${inner}</${tag}>`;

      switch (f.type) {
        case "cards":
        case "choice":
        case "sizes": {
          const opts = (val(f.options, state) || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
          const items = opts
            .map(
              (o) => `
              <label class="hfh-option${f.type === "cards" ? " hfh-option--card" : ""}">
                <input type="radio" name="${id}" value="${esc(o.value)}" data-name="${f.name}" ${value === o.value ? "checked" : ""} aria-describedby="${describedBy}">
                <span class="hfh-option__box">
                  <span class="hfh-option__label">${esc(o.label)}</span>
                  ${o.desc ? `<span class="hfh-option__desc">${esc(o.desc)}</span>` : ""}
                </span>
              </label>`
            )
            .join("");
          return wrap(
            `<legend class="hfh-label">${label}${req}</legend>${helpHtml}
             <div class="hfh-options hfh-options--${f.type}">${items}</div>${errHtml}`,
            "fieldset"
          );
        }
        case "select": {
          const opts = f.options.map((o) => `<option ${value === o ? "selected" : ""}>${esc(o)}</option>`).join("");
          return wrap(
            `<label class="hfh-label" for="${id}">${label}${req}</label>${helpHtml}
             <select id="${id}" class="hfh-input" data-name="${f.name}" aria-describedby="${describedBy}">
               <option value="">Choose one…</option>${opts}
             </select>${errHtml}`
          );
        }
        case "textarea": {
          const len = (value || "").length;
          return wrap(
            `<label class="hfh-label" for="${id}">${label}${req}</label>${helpHtml}
             <textarea id="${id}" class="hfh-input" rows="${f.rows || 4}" maxlength="${f.max || 2000}" data-name="${f.name}"
               placeholder="${esc(f.placeholder || "")}" aria-describedby="${describedBy}">${esc(value || "")}</textarea>
             ${f.max ? `<p class="hfh-counter" aria-live="polite"><span>${len}</span> / ${f.max}</p>` : ""}${errHtml}`
          );
        }
        case "address": {
          const a = value || {};
          const states = STATES.map(([c, n]) => `<option value="${c}" ${a.state === c ? "selected" : ""}>${n}</option>`).join("");
          const ac = f.name.includes("school") ? "off" : "shipping";
          return wrap(
            `<legend class="hfh-label">${label}${req}</legend>${helpHtml}
             <div class="hfh-address">
               <label class="hfh-sub hfh-span-2">Street address
                 <input class="hfh-input" data-name="${f.name}.street" value="${esc(a.street || "")}" autocomplete="${ac} address-line1"></label>
               <label class="hfh-sub hfh-span-2">Apt, suite, room <span class="hfh-optional">(optional)</span>
                 <input class="hfh-input" data-name="${f.name}.street2" value="${esc(a.street2 || "")}" autocomplete="${ac} address-line2"></label>
               <label class="hfh-sub hfh-span-2">City
                 <input class="hfh-input" data-name="${f.name}.city" value="${esc(a.city || "")}" autocomplete="${ac} address-level2"></label>
               <label class="hfh-sub">State
                 <select class="hfh-input" data-name="${f.name}.state" autocomplete="${ac} address-level1"><option value="">State…</option>${states}</select></label>
               <label class="hfh-sub">ZIP code
                 <input class="hfh-input" data-name="${f.name}.zip" value="${esc(a.zip || "")}" inputmode="numeric" maxlength="10" autocomplete="${ac} postal-code"></label>
             </div>
             <p class="hfh-help">Open to the contiguous US only (not available in Alaska or Hawaii).</p>${errHtml}`,
            "fieldset"
          );
        }
        case "file": {
          return wrap(
            `<span class="hfh-label">${label}${req}</span>${helpHtml}
             <div class="hfh-file">
               <div class="hfh-file__preview" ${photo ? "" : "hidden"}>
                 <img alt="Selected photo" src="${photo ? photo.url : ""}">
                 <div><p class="hfh-file__name">${photo ? esc(photo.file.name) : ""}</p>
                 <button type="button" class="hfh-link" data-action="remove-photo">Remove photo</button></div>
               </div>
               <label class="hfh-file__drop" ${photo ? "hidden" : ""}>
                 <input type="file" accept="image/*" class="hfh-file__input" data-file aria-describedby="${describedBy}">
                 <span class="hfh-file__icon" aria-hidden="true">
                   <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8.5" cy="10" r="1.5"/><path d="M21 16l-5-5-8 8"/></svg>
                 </span>
                 <span><strong>Add a photo</strong><br><span class="hfh-help">Tap to choose a file</span></span>
               </label>
             </div>${errHtml}`
          );
        }
        case "checkbox": {
          let text = label;
          if (f.rulesLink && cfg.rulesUrl) {
            text = label.replace("official giveaway rules", `<a href="${esc(cfg.rulesUrl)}" target="_blank" rel="noopener">official giveaway rules</a>`);
          }
          return wrap(
            `<label class="hfh-check">
               <input type="checkbox" data-name="${f.name}" ${value ? "checked" : ""} aria-describedby="${describedBy}">
               <span class="hfh-check__box" aria-hidden="true"></span>
               <span>${text}${f.required ? "" : ' <span class="hfh-optional">(optional)</span>'}</span>
             </label>${errHtml}`
          );
        }
        case "summary":
          return `<div class="hfh-summary" data-key="${key}">${summaryHtml()}</div>`;
        default: {
          const type = f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text";
          return wrap(
            `<label class="hfh-label" for="${id}">${label}${req}</label>${helpHtml}
             <input id="${id}" class="hfh-input" type="${type}" data-name="${f.name}" value="${esc(value || "")}"
               placeholder="${esc(f.placeholder || "")}" autocomplete="${esc(val(f.autocomplete, state) || (type === "email" ? "email" : "on"))}"
               ${type === "tel" ? 'inputmode="tel"' : ""} aria-describedby="${describedBy}">${errHtml}`
          );
        }
      }
    }

    function summaryHtml() {
      const s = state;
      const t = s.teacher || {}, r = s.rescue || {}, n = s.nominator || {}, st = s.story || {};
      const yn = OPTION_TEXT.yesno;
      const groups = [];
      groups.push(["start", "Nomination", [
        ["Nominating", OPTION_TEXT.nominatorType[s.nominatorType]],
        ["Category", OPTION_TEXT.category[s.category]],
      ]]);
      if (s.nominatorType === "other") {
        groups.push(["you", "About you", [["Name", n.name], ["Email", n.email], ["Relationship", n.relationship]]]);
      }
      if (s.category === "teacher") {
        groups.push(["teacher", isSelf(s) ? "About you" : "Teacher", [
          ["Name", t.name], ["Email", t.email], ["Phone", t.phone], ["Grade / subject", t.gradeSubject],
          ["School", t.school], ["School address", fmtAddress(t.schoolAddress)],
        ]]);
        groups.push(["classroom", "Classroom", [
          ["School allows animals", yn[t.schoolAllowsAnimals]],
          ["Keeps a reptile", OPTION_TEXT.reptileStatus[t.reptileStatus] + (t.reptileSpecies ? ` (${t.reptileSpecies})` : "")],
          ["Photo", photo ? photo.file.name : ""],
          ["Ship to", t.shipToSchool === "no" ? fmtAddress(t.shippingAddress) : t.shipToSchool === "unsure" ? SHIP_UNSURE_SUMMARY : "School address"],
        ]]);
      }
      if (s.category === "rescue") {
        groups.push(["rescue", "Rescue", [
          ["Rescue", r.name], ["Website / social", r.web],
          ["501(c)(3)", r.registered === "yes" ? `Yes${r.registrationNumber ? ` (EIN ${r.registrationNumber})` : ""}` : "Not sure"],
          ["Animals in care", r.animalsInCare], ["Photo", photo ? photo.file.name : ""],
        ]]);
        groups.push(["contact", "Contact & shipping", [
          ["Contact", r.contactName], ["Email", r.contactEmail], ["Phone", r.phone],
          ["Ship to", !isSelf(s) && r.shippingKnown === "unsure" ? SHIP_UNSURE_SUMMARY : fmtAddress(r.shippingAddress)],
        ]]);
      }
      groups.push(["story", "The nomination", [
        ["How it would help", st.why],
        ["Animal", st.species === "Other" ? st.speciesOther : st.species],
        ["Setup size", st.size], ["Notes", st.sizeNote],
      ]]);
      return groups
        .map(([stepId, title, rows]) => `
          <div class="hfh-summary__group">
            <div class="hfh-summary__head"><h4>${esc(title)}</h4>
              <button type="button" class="hfh-link" data-goto="${stepId}">Edit</button></div>
            <dl>${rows.filter(([, v]) => v).map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join("")}</dl>
          </div>`)
        .join("");
    }

    // ---- step rendering & navigation ----
    function updateProgress() {
      const list = activeSteps();
      const idx = Math.max(0, list.findIndex((st) => st.id === current));
      // The total isn't known until both questions on step 1 are answered.
      const known = Boolean(state.category && state.nominatorType);
      $(".hfh-stepcount").textContent = known ? `Step ${idx + 1} of ${list.length}` : "Step 1";
      $(".hfh-progress__bar").style.width = known ? `${Math.round(((idx + 1) / list.length) * 100)}%` : "6%";
    }

    function render(focusHeading) {
      const list = activeSteps();
      let idx = list.findIndex((st) => st.id === current);
      if (idx < 0) { idx = 0; current = list[0].id; }
      const step = list[idx];

      form.innerHTML = `
        <h3 class="hfh-step-title" tabindex="-1">${esc(val(step.title, state))}</h3>
        ${step.subtitle ? `<p class="hfh-step-sub">${esc(step.subtitle)}</p>` : ""}
        <div class="hfh-fields">${step.fields.map((f, i) => fieldHtml(f, i)).join("")}</div>
        <div class="hfh-hp" aria-hidden="true"><label>Leave this empty<input type="text" name="website" tabindex="-1" autocomplete="off"></label></div>
        <div class="hfh-formerror" role="alert" hidden></div>
        <div class="hfh-nav">
          ${idx > 0 ? '<button type="button" class="hfh-btn hfh-btn--ghost" data-action="back">Back</button>' : "<span></span>"}
          <button type="submit" class="hfh-btn hfh-btn--primary">${step.id === "review" ? "Submit nomination" : "Continue"}</button>
        </div>
        ${step.id === "review" && cfg.privacyUrl ? `<p class="hfh-fine">We'll only use this information to run Habitats for Heroes. See our <a href="${esc(cfg.privacyUrl)}" target="_blank" rel="noopener">privacy policy</a>.</p>` : ""}`;
      refresh();
      if (focusHeading) {
        const h = form.querySelector(".hfh-step-title");
        h.focus({ preventScroll: true });
        const top = root.getBoundingClientRect().top + window.scrollY - 100;
        if (root.getBoundingClientRect().top < 0) window.scrollTo({ top, behavior: "smooth" });
      }
    }

    function currentStep() {
      return stepById(current);
    }

    // Shows/hides conditional fields and refreshes state-dependent labels on the current step.
    function refresh() {
      updateProgress();
      currentStep().fields.forEach((f, i) => {
        const el = form.querySelector(`[data-key="${i}"]`);
        if (!el) return;
        el.hidden = f.when ? !f.when(state) : false;
        if (typeof f.label === "function" && f.type !== "checkbox") {
          const labelEl = el.querySelector(".hfh-label");
          if (labelEl) labelEl.firstChild.textContent = f.label(state);
        }
      });
    }

    function isVisible(f) {
      return !f.when || f.when(state);
    }

    function fieldError(f) {
      const v = f.name ? get(state, f.name) : undefined;
      const empty = (x) => x === undefined || x === null || String(x).trim() === "";
      switch (f.type) {
        case "address": {
          const a = v || {};
          if (empty(a.street) || empty(a.city)) return "Please enter the full street address and city.";
          if (empty(a.state)) return "Please choose a state.";
          if (!ZIP_RE.test(String(a.zip || "").trim())) return "Please enter a valid 5-digit ZIP code.";
          return "";
        }
        case "checkbox":
          return f.required && v !== true ? "Please check this box to continue." : "";
        case "cards":
        case "choice":
        case "sizes":
        case "select": {
          if (f.required && empty(v)) return "Please choose an option.";
          // An earlier answer can remove an option (e.g. switching to a self-nomination drops "I'm not sure").
          const opts = val(f.options, state) || [];
          if (!empty(v) && f.type !== "select" && !opts.some((o) => (typeof o === "string" ? o : o.value) === v)) return "Please choose an option.";
          return "";
        }
        case "file":
        case "summary":
          return "";
        default:
          if (f.required && empty(v)) return "This field is required.";
          if (f.type === "email" && !empty(v) && !EMAIL_RE.test(String(v).trim())) return "Please enter a valid email address.";
          if (f.type === "tel" && !empty(v) && String(v).replace(/\D/g, "").length < 10) return "Please enter a 10-digit phone number.";
          return "";
      }
    }

    function showError(key, message) {
      const el = form.querySelector(`[data-key="${key}"]`);
      if (!el) return;
      const err = el.querySelector(".hfh-error");
      if (err) {
        err.textContent = message;
        err.hidden = !message;
      }
      el.classList.toggle("hfh-field--invalid", Boolean(message));
      el.querySelectorAll("input, select, textarea").forEach((i) => {
        if (message) i.setAttribute("aria-invalid", "true");
        else i.removeAttribute("aria-invalid");
      });
    }

    function validateStep() {
      let firstBad = null;
      currentStep().fields.forEach((f, i) => {
        const msg = isVisible(f) ? fieldError(f) : "";
        showError(i, msg);
        if (msg && firstBad === null) firstBad = i;
      });
      if (firstBad !== null) {
        const el = form.querySelector(`[data-key="${firstBad}"]`);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = el.querySelector("input:not([type=hidden]), select, textarea");
        if (input) input.focus({ preventScroll: true });
        return false;
      }
      return true;
    }

    function go(delta) {
      const list = activeSteps();
      const idx = list.findIndex((st) => st.id === current);
      const next = list[Math.min(Math.max(idx + delta, 0), list.length - 1)];
      current = next.id;
      saveDraft();
      render(true);
    }

    // ---- events ----
    form.addEventListener("input", (e) => {
      const t = e.target;
      if (!t.dataset.name) return;
      const v = t.type === "checkbox" ? t.checked : t.value;
      set(state, t.dataset.name, v);
      const counter = t.parentElement.querySelector(".hfh-counter span");
      if (counter) counter.textContent = t.value.length;
      // Clear the field's error as soon as it's fixed.
      const field = t.closest("[data-key]");
      if (field && field.classList.contains("hfh-field--invalid")) {
        const f = currentStep().fields[field.dataset.key];
        if (!fieldError(f)) showError(field.dataset.key, "");
      }
      refresh();
      saveDraft();
    });
    // Radios, checkboxes and selects: some older browsers only fire `change` for these.
    form.addEventListener("change", (e) => {
      if (e.target.matches("select[data-name], input[type=radio][data-name], input[type=checkbox][data-name]")) {
        const t = e.target;
        set(state, t.dataset.name, t.type === "checkbox" ? t.checked : t.value);
        const field = t.closest("[data-key]");
        if (field) showError(field.dataset.key, "");
        refresh();
        saveDraft();
      }
      if (e.target.matches("[data-file]")) onPhoto(e.target);
    });

    root.addEventListener("click", (e) => {
      const btn = e.target.closest("button");
      if (!btn || !root.contains(btn)) return;
      if (btn.dataset.action === "back") go(-1);
      if (btn.dataset.action === "restart") {
        storage.clear();
        state = { consent: {} };
        photo = null;
        current = "start";
        $(".hfh-restored").hidden = true;
        render(true);
      }
      if (btn.dataset.action === "remove-photo") {
        if (photo) URL.revokeObjectURL(photo.url);
        photo = null;
        render(false);
      }
      if (btn.dataset.action === "another") {
        state = { consent: {} };
        photo = null;
        photoTask = null;
        finished = false;
        current = "start";
        root.querySelector(".hfh-card").classList.remove("hfh-card--done");
        render(true);
      }
      if (btn.dataset.goto) {
        current = btn.dataset.goto;
        render(true);
      }
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (submitting || !validateStep()) return;
      if (current !== "review") return go(1);
      await submit();
    });

    function onPhoto(input) {
      // Tracked so submit() can wait for a photo that's still being resized.
      photoTask = handlePhoto(input);
    }

    async function handlePhoto(input) {
      const file = input.files && input.files[0];
      const field = input.closest("[data-key]");
      if (!file) return;
      let type = file.type;
      if (!type && /\.(heic|heif)$/i.test(file.name)) type = "image/heic";
      if (!PHOTO_TYPES.includes(type)) {
        showError(field.dataset.key, "Please choose a JPG, PNG, WebP or HEIC photo.");
        input.value = "";
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        showError(field.dataset.key, "That photo is too large — please choose one under 20 MB.");
        input.value = "";
        return;
      }
      let prepared = await compress(file.type ? file : new File([file], file.name, { type }));
      if (prepared.size > 5 * 1024 * 1024) {
        showError(field.dataset.key, "That photo is too large — please choose one under 5 MB.");
        input.value = "";
        return;
      }
      if (photo) URL.revokeObjectURL(photo.url);
      photo = { file: prepared, url: URL.createObjectURL(prepared) };
      // Only redraw if the person is still looking at the photo field.
      if (!finished && currentStep().fields.some((f) => f.type === "file")) render(false);
    }

    // Shrinks big phone photos before upload (max 1600px, JPEG). Formats the browser can't decode are sent as-is.
    async function compress(file) {
      if (!/^image\/(jpeg|png|webp)$/.test(file.type) || !window.createImageBitmap) return file;
      try {
        const bmp = await createImageBitmap(file);
        const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
        if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(bmp.width * scale);
        canvas.height = Math.round(bmp.height * scale);
        canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.82));
        return blob ? new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
      } catch {
        return file;
      }
    }

    async function submit() {
      const errorBox = form.querySelector(".hfh-formerror");
      const button = form.querySelector(".hfh-btn--primary");
      errorBox.hidden = true;
      // A test page can supply its own submit handler (see scripts/build-test-page.mjs).
      const override = typeof window.hfhSubmitOverride === "function" ? window.hfhSubmitOverride : null;
      if (!cfg.endpoint && !override) {
        errorBox.textContent = "This form isn't connected yet. (Set the submission URL in the theme editor.)";
        errorBox.hidden = false;
        return;
      }
      submitting = true;
      button.disabled = true;
      button.textContent = "Submitting…";
      if (photoTask) await photoTask;

      // Drop answers from branches that no longer apply (e.g. switched from teacher to rescue).
      const payload = JSON.parse(JSON.stringify(state));
      if (payload.nominatorType !== "other") delete payload.nominator;
      if (payload.category !== "teacher") delete payload.teacher;
      if (payload.category !== "rescue") delete payload.rescue;
      payload._hp = form.querySelector('.hfh-hp input').value;
      payload._elapsedMs = Date.now() - startedAt;
      payload._page = location.href.split("?")[0];

      const body = new FormData();
      body.append("data", JSON.stringify(payload));
      if (photo) body.append("photo", photo.file, photo.file.name);

      try {
        let data;
        if (override) {
          data = await override(payload, photo ? photo.file : null);
        } else {
          const res = await fetch(cfg.endpoint, { method: "POST", body });
          data = await res.json().catch(() => ({}));
          if (!res.ok) data.ok = false;
        }
        if (!data || !data.ok) {
          data = data || {};
          const list = (data.errors || []).map((m) => `<li>${esc(m)}</li>`).join("");
          errorBox.innerHTML = `<strong>${esc(data.error || "Something went wrong. Please try again.")}</strong>${list ? `<ul>${list}</ul>` : ""}`;
          errorBox.hidden = false;
          errorBox.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        storage.clear();
        done(data.id);
      } catch {
        errorBox.innerHTML = "<strong>We couldn't reach our server.</strong> Check your connection and try again — your answers are saved.";
        errorBox.hidden = false;
      } finally {
        submitting = false;
        if (button.isConnected) {
          button.disabled = false;
          button.textContent = "Submit nomination";
        }
      }
    }

    function done(id) {
      finished = true;
      const self = isSelf(state);
      root.querySelector(".hfh-card").classList.add("hfh-card--done");
      $(".hfh-progress__bar").style.width = "100%";
      $(".hfh-stepcount").textContent = "";
      $(".hfh-restored").hidden = true;
      form.innerHTML = `
        <div class="hfh-done">
          <div class="hfh-done__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>
          </div>
          <h3 class="hfh-step-title" tabindex="-1">Thank you — your ${self ? "application" : "nomination"} is in!</h3>
          <p>Our team reviews nominations every month. If ${self ? "you're" : "your nominee is"} selected, we'll reach out by email.</p>
          ${id && id !== "HFH-RECEIVED" ? `<p class="hfh-ref">Reference number: <strong>${esc(id)}</strong></p>` : ""}
          <button type="button" class="hfh-btn hfh-btn--ghost" data-action="another">Nominate someone else</button>
        </div>`;
      form.querySelector(".hfh-step-title").focus({ preventScroll: true });
      root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    render(false);
  }

  function init() {
    document.querySelectorAll("[data-hfh-form]").forEach(mount);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  // Re-mount when the section is added or edited in the Shopify theme editor.
  document.addEventListener("shopify:section:load", init);
})();
